import { useEffect, useRef, useState } from "react";
import { archetypeColorIndexes, processArchetypeData , IArchetypeData} from "../../../types/IRolesPlayedUtils";
import { PolarAreaChart} from './RolesPlayedUtils'
import {ArchetypeItem, ArchetypeSidebar, ConnectionContainer, ConnectionSVG, EyeIconWrapper, HeaderContent} from "../../../styled-components/RolesPlayedUtilsStyles"
import * as d3 from "d3";
import EyeIcon from "../../../assets/icons/VisibleON.png";
import apiUrls from "../../../config/apiUrls";
import { fetchData } from "../../../services/api";
import handleApiResponse from "../../../utils/handleApiResponse";
import IApiResponse from "../../../types/IApiResponse";
import { useLoading } from "../../../contexts/LoadingContext";
import { ArchetypeInfoDrawer } from "../../../pages/Chemistry/UIContent";
import InfoIcon from "../../../assets/icons/infoicon.svg";
import { usePlayerSearch } from "../../../contexts/SearchPlayersContext";
import { styled } from "styled-components";
import LabelText from "../../../ui/LabelText";
import SecondaryButton from "../../../ui/SecondaryButton";
import PlayerInitialSearch from "../../PlayerLanding/PlayerSearch/PlayerInitialSearch";
import { IArchetypeInfo, ITypicalPlayerExample } from "../../PlayerLanding/PlayerSearch/PlayerSearchInit";
import { BackToTeamChemistryBtn } from "../../../pages/Chemistry/StyledChemistryElements";
import ResetIcon from "../../../assets/icons/Reset.png";
import { ModuleSpinner } from "../../../ui/LoadingSpinner/ModuleSpinner";


const Line = styled.div`
    width: -webkit-fill-available;
    height: 2px;
    border: 1px solid #DADADA;
`

interface IRolesPlayedProps {
  playerName: string;
  playerId: number;
  seasonId: number;
}

export const RolesPlayed: React.FC<IRolesPlayedProps> = ({ playerName, playerId, seasonId }) => {
  
  const {playerSearchOpen , setIsPlayerSearchOpen , comparePlayerImageUrl,comparePlayerName , comparePlayerId ,
    setComparePlayerId , setComparePlayerName , setComparePlayerImageUrl } = usePlayerSearch()
  const [archetypeData, setArchetypeData] = useState<IArchetypeData[]>([]);
  const [comparisonPlayerData, setComparisonPlayerData] = useState<IArchetypeData[]>([]);
  const [activeArchetype, setActiveArchetype] = useState<number>(0);
  const totalMinutes = archetypeData?.reduce((sum, archetype) => sum + archetype.minutes, 0);
  const activeItemRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const connectionRef = useRef<SVGSVGElement>(null);
  const {startLoading, stopLoading} = useLoading();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [activeArcheTypeId, setActiveArcheTypeId] = useState<number>(0);
  const [activeArcheTypeName, setActiveArcheTypeName] = useState<string>("");
  const [isPlayerSipderGraphLoading , setIsPlayerSpiderGraphLoading] = useState<boolean>(false)
  
  const fetchSpiderData = async (playerId : number , seasonId : number , isComparison : boolean) => {
      setIsPlayerSpiderGraphLoading(true);
      const response: IApiResponse<IArchetypeData[]> = await fetchData(`${apiUrls["GetPlayerSpiderGraph"]}/${playerId}?seasonId=${seasonId}`);
      handleApiResponse(
        response,
        (spiderData) => {
          setIsPlayerSpiderGraphLoading(false);
          const sortedSpiderData = spiderData?.sort((a,b) => b?.minutes - a?.minutes)
          if(isComparison){
            setComparisonPlayerData(spiderData)
          }else{
            setArchetypeData(sortedSpiderData);
            setActiveArchetype((sortedSpiderData[0]?.archetypeId! + sortedSpiderData[0]?.competitionId + sortedSpiderData[0]?.teamId + sortedSpiderData[0]?.seasonId));
            setActiveArcheTypeId(sortedSpiderData[0]?.archetypeId!);
            setActiveArcheTypeName(sortedSpiderData[0]?.archetypeName!);
          }
        },
        (error) => {
          setIsPlayerSpiderGraphLoading(false);
          console.error("Error fetching Player Spider Data:", error);
        }
      );
    };

  useEffect(() => {
    if (playerId > 0 && seasonId > 0) {
      fetchSpiderData(playerId , seasonId , false);
    }
  }, [playerId, seasonId]);

  useEffect(()=>{
    if(comparePlayerId! > 0 && comparePlayerId !== playerId){
      fetchSpiderData(comparePlayerId,0 , true)
    }
  },[comparePlayerId,playerId])



  useEffect(() => {
    if (!activeItemRef.current || !chartRef.current || !connectionRef.current) return;
    const itemRect = activeItemRef.current.getBoundingClientRect();
    const chartRect = chartRef.current.getBoundingClientRect();
    const containerRect = connectionRef.current.getBoundingClientRect();
    const startX = itemRect.right - containerRect.left;
    const startY = itemRect.top + itemRect.height / 2 - containerRect.top;
    const chartCenterX = chartRect.left + chartRect.width / 2 - containerRect.left;
    const chartCenterY = chartRect.top + chartRect.height / 2 - containerRect.top;
    const chartRadius = Math.min(chartRect.width, chartRect.height) / 2 - 20;
    const angleToCenter = Math.atan2(chartCenterY - startY, chartCenterX - startX);
    const adjustedAngle = angleToCenter - Math.PI / 6;
    const endX = chartCenterX - Math.cos(adjustedAngle) * chartRadius;
    const endY = chartCenterY - Math.sin(adjustedAngle) * chartRadius;
    const path = `
      M ${startX} ${startY}
      C ${startX + 50} ${startY},
        ${endX - 50} ${endY},
        ${endX} ${endY}
    `;
    const svg = d3.select(connectionRef.current);
    svg.selectAll("*").remove();
    svg
      .append("path")
      .attr("d", path)
      .style("fill", "none")
      .style(
        "stroke",
        archetypeColorIndexes[archetypeData?.findIndex((a) => (a?.archetypeId + a?.competitionId + a?.teamId + a?.seasonId) === activeArchetype)! % archetypeColorIndexes.length]
      )
      .style("stroke-width", "3")
      .style("opacity", "0.6");
  }, [activeArchetype, archetypeData]);

    const handleResetTeamArchType = () => {
      setComparePlayerName("");
      setComparePlayerImageUrl("");
      setComparePlayerId(0)
      setComparisonPlayerData([])
  }

  return (
    <>
    <div className="roles-played-master">
      <div className="roles-played-header">
        <h3>Roles played</h3>
        <div className="roles-player-player-header">
          <p><p className="roles-player-player-name"></p>{playerName}</p>
          {comparePlayerId === 0 && (<p><p className="role-player-role-avg"></p>Role average</p>)}
        </div>
      </div>
      <ConnectionContainer>
        {archetypeData?.length === 1 && 
          <div className="single-archetype-container">
            <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
              <p className="archetype-name-sidebar">{archetypeData?.[0]?.archetypeName}</p>
              <div className='attributesonpitch-info-icon' style={{position:'static'}}>
                  <img src={InfoIcon} alt="archetpe-info" className="archetype-info-icon" onClick={() => setSidebarOpen(true)} />   
              </div>
            </div>
            <p className="archetype-minutes-sidebar">
              <span className="archetype-minutes-number-sidebar">MINS PLAYED</span>
              {archetypeData?.[0]?.minutes.toFixed(0)}
            </p>
            <div className="archtype-main">
                <div className="archtype-info-container" style={{left:'0px'}}>
                    <div className="archetype-info-content" style={{paddingLeft:'0px',paddingTop:'10px'}}>
                      <Line/>
                        <div className="archtype-info">
                            <div className="archtype-tag" style={{background:'transparent',border:'1px solid black'}}></div>
                            {comparePlayerImageUrl && comparePlayerName ? (
                                <div style={{display:'flex',alignItems:'center',gap:'4px'}}>
                                  =  <img src={comparePlayerImageUrl} width={18} height={18} alt="club-logo" className="club-logo" />
                                  <LabelText className="archtype-tag-text">{comparePlayerName}</LabelText>
                                </div>
                            ):(
                              <LabelText className="archtype-tag-text">=  Av. for this style of play</LabelText>
                            )}
                        </div>
                        <div className="archtype-info">
                            <SecondaryButton label="Compare to other player" className="compare-btn" onClick={()=>{setIsPlayerSearchOpen(true)}}/>
                        </div>
                      </div>
                </div>   
          </div>
          </div>
        }
        <div className="archetype-master-container">
          {archetypeData?.length! > 1 && 
            <ArchetypeSidebar isSingleArchetype={archetypeData?.length === 1}>
              {archetypeData?.map((archetype, index) => {
                const heightPercentage = (archetype.minutes / totalMinutes!) * 100;
                const colorIndex = index % archetypeColorIndexes.length;
                return (
                  <ArchetypeItem
                    key={(archetype?.archetypeId + archetype?.competitionId + archetype?.teamId + archetype?.seasonId)}
                    heightPercentage={heightPercentage}
                    ref={(archetype?.archetypeId + archetype?.competitionId + archetype?.teamId + archetype?.seasonId) === activeArchetype ? activeItemRef : null}
                    colorIndex={colorIndex}
                    isSingleArchetype={archetypeData?.length === 1}
                    isFirst={index === 0}
                    isLast={index === archetypeData.length - 1}
                  >
                    <div className="spider-info-label">
                    <EyeIconWrapper
                      isActive={activeArchetype === (archetype?.archetypeId + archetype?.competitionId + archetype?.teamId + archetype?.seasonId)}
                      onClick={() => {
                        setActiveArchetype(archetype?.archetypeId + archetype?.competitionId + archetype?.teamId + archetype?.seasonId);
                        setActiveArcheTypeId(archetype?.archetypeId);
                        setActiveArcheTypeName(archetype?.archetypeName);
                      }
                      }
                    >
                      <img src={EyeIcon} alt="expand-eye-icon" className="archetypes-exapnd-eye-icon" />
                    </EyeIconWrapper>
                    <div className="archetype-name-container">
                      <p className="archetype-name-sidebar">{archetype?.archetypeName}</p>
                      
                      <p className="archetype-minutes-sidebar">
                        <span className="archetype-minutes-number-sidebar">MINS PLAYED</span>
                        {archetype?.minutes.toFixed(0)}
                      </p>
                    </div>
                    {archetype?.archetypeId + archetype?.competitionId + archetype?.teamId + archetype?.seasonId === activeArchetype && (
                        <div className='attributesonpitch-info-icon' style={{position:'static'}}>
                              <img src={InfoIcon} alt="archetpe-info" className="archetype-info-icon" onClick={() => setSidebarOpen(true)} /> 
                        </div>
                    )}
                    </div>
                    {activeArchetype === (archetype?.archetypeId + archetype?.competitionId + archetype?.teamId + archetype?.seasonId) && (
                          <div className="archtype-main">
                                <div className="archtype-info-container">
                                    <div className="archetype-info-content">
                                      <Line/>
                                        <div className="archtype-info">
                                            <div className="archtype-tag" style={{background:'transparent',border:'1px solid black'}}></div>
                                            {comparePlayerImageUrl && comparePlayerName ? (
                                                <div style={{display:'flex',alignItems:'center',gap:'4px'}}>
                                                  =  <img src={comparePlayerImageUrl} width={18} height={18} alt="club-logo" className="club-logo" />
                                                  <LabelText className="archtype-tag-text">{comparePlayerName}</LabelText>
                                                </div>
                                            ):(
                                              <LabelText className="archtype-tag-text">=  Av. for this style of play</LabelText>
                                            )}
                                        </div>
                                        <div className="archtype-info">
                                            <SecondaryButton label="Compare to other player" className="compare-btn" onClick={()=>{setIsPlayerSearchOpen(true)}}/>
                                        </div>
                                      </div>
                                </div>   
                          </div>
                      )}
                  </ArchetypeItem>
                );
              })}
            </ArchetypeSidebar>
          }
          <div className="roles-played-spidergram" ref={chartRef}>
            {archetypeData
              ?.filter((archetype) => (archetype?.archetypeId + archetype?.competitionId + archetype?.teamId + archetype?.seasonId) === activeArchetype)
              ?.map((archetype) => (
                <PolarAreaChart
                  key={(archetype?.archetypeId + archetype?.competitionId + archetype?.teamId + archetype?.seasonId)}
                  data={processArchetypeData(archetype)}
                  averageData={processArchetypeData(comparisonPlayerData[0]!)}
                  height={600}
                  width={682}
                  colorIndex={archetypeData.findIndex((a) => (a?.archetypeId + a?.competitionId + a?.teamId + a?.seasonId) === activeArchetype) % archetypeColorIndexes.length}
                  isComparisonBlackLine={comparePlayerId ? true : false}
                />
              ))}
          </div>
          <div className="reset-team-archtype">
              <BackToTeamChemistryBtn onClick={() => handleResetTeamArchType()} disabled={comparePlayerId === 0}>
              <img src={ResetIcon} alt="reset icon" /> Reset player
            </BackToTeamChemistryBtn>  
          </div>
          <div className="attributes-archtype-main-info">
            <div className="archetype-info-content">
                <div className="archtype-info">
                    <div className="archtype-tag" style={{background:'#64ADDB'}}></div>
                    <LabelText className="archtype-tag-text">Defensive</LabelText>
                </div>
                <div className="archtype-info">
                    <div className="archtype-tag" style={{background:'#C9F0B5'}}></div>
                    <LabelText className="archtype-tag-text">Build up play</LabelText>
                </div>
                <div className="archtype-info">
                    <div className="archtype-tag" style={{background:'#FFC2C2'}}></div>
                    <LabelText className="archtype-tag-text">Final third</LabelText>
                </div>
              </div>
          </div>
        </div>
        {archetypeData?.length! > 1 && <ConnectionSVG ref={connectionRef} />}
      </ConnectionContainer>
      {isPlayerSipderGraphLoading && <ModuleSpinner/>}
       {archetypeData?.length > 0 && <ArchetypeInfoDrawer
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              //archetypeName={archetypeData[0]?.archetypeName!}
              //activeArchetypeID={archetypeData[0]?.archetypeId!}
              archetypeName={activeArcheTypeName}
              activeArchetypeID={activeArcheTypeId}
            />}
    </div>
    {playerSearchOpen && <PlayerInitialSearch open={playerSearchOpen} setIsOpen={setIsPlayerSearchOpen} archtypeId={archetypeData[0]?.archetypeId!} headerLabel="Comparison Player"/>}
    
    </>
  );
};
